/**
 * ConceptCard Component
 *
 * Displays a programming concept with its details, code snippets,
 * and language-specific implementations.
 *
 * @module concept-graph/components
 */

import React, { useState } from 'react';
import type { Concept, ProgrammingLanguage } from '../types';
import { CodeSnippet } from './CodeSnippet';
import { LanguageSelector } from './LanguageSelector';
import { PhilosophySection } from './PhilosophySection';

export interface ConceptCardProps {
  concept: Concept;
  initialLanguage?: ProgrammingLanguage;
  showRelationships?: boolean;
  onRelatedClick?: (conceptId: string) => void;
  className?: string;
}

/**
 * Display a programming concept card
 */
type TabMode = 'overview' | 'syntax' | 'philosophy';

export function ConceptCard({
  concept,
  initialLanguage,
  showRelationships = true,
  onRelatedClick,
  className = ''
}: ConceptCardProps) {
  // Determine initial language
  const availableLanguages = Object.keys(concept.implementations) as ProgrammingLanguage[];
  const [selectedLanguage, setSelectedLanguage] = useState<ProgrammingLanguage>(
    initialLanguage && availableLanguages.includes(initialLanguage)
      ? initialLanguage
      : availableLanguages[0]
  );

  // Tab navigation state - default to philosophy if available, else overview
  const [activeTab, setActiveTab] = useState<TabMode>(
    concept.philosophy ? 'philosophy' : 'overview'
  );

  const implementation = concept.implementations[selectedLanguage];

  if (!implementation) {
    return (
      <div className="p-4 border border-red-200 bg-red-50 rounded-lg">
        <p className="text-red-700">No implementation available for {selectedLanguage}</p>
      </div>
    );
  }

  // Difficulty stars
  const difficultyStars = '★'.repeat(concept.difficulty) + '☆'.repeat(5 - concept.difficulty);

  return (
    <div
      className={`concept-card bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden ${className}`}
      data-testid={`concept-card-${concept.id}`}
    >
      {/* Header */}
      <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-orange-50 to-white">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">{concept.name}</h2>
            <div className="mt-1 flex items-center gap-3">
              <span className="px-2 py-0.5 text-xs bg-white border border-slate-200 rounded-full text-slate-600">
                {concept.category.replace(/_/g, ' ')}
              </span>
              <span className="text-sm text-orange-600" title={`Difficulty: ${concept.difficulty}/5`}>
                {difficultyStars}
              </span>
            </div>
          </div>
          <LanguageSelector
            selectedLanguage={selectedLanguage}
            availableLanguages={availableLanguages}
            onLanguageChange={setSelectedLanguage}
          />
        </div>

        <p className="mt-4 text-slate-600 leading-relaxed">{concept.description}</p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-slate-200 bg-white">
        <div className="flex gap-1 px-6">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'overview'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('syntax')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'syntax'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300'
            }`}
          >
            Syntax
          </button>
          {concept.philosophy && (
            <button
              onClick={() => setActiveTab('philosophy')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-1 ${
                activeTab === 'philosophy'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300'
              }`}
            >
              <span className="text-base">🧠</span>
              Philosophy
            </button>
          )}
        </div>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="p-6 space-y-6">
          {/* Quick Language Comparison */}
          <div>
            <h3 className="text-sm font-semibold text-slate-900 mb-3">Quick Comparison</h3>
            <div className="grid grid-cols-1 gap-3">
              {availableLanguages.map(lang => {
                const impl = concept.implementations[lang];
                const firstPattern = impl?.syntaxPatterns?.[0];
                return (
                  <div
                    key={lang}
                    className={`p-3 rounded-lg border transition-colors ${
                      lang === selectedLanguage
                        ? 'bg-orange-50 border-orange-200'
                        : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="font-semibold text-slate-900 text-sm mb-1">{lang}</div>
                    {firstPattern && (
                      <>
                        <code className="text-xs font-mono text-slate-700 block">
                          {firstPattern.syntax}
                        </code>
                        <p className="text-xs text-slate-600 mt-1">{firstPattern.explanation}</p>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Relationships */}
          {showRelationships && concept.relationships && (
            <div>
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Related Concepts</h3>

              {concept.relationships.requires && concept.relationships.requires.length > 0 && (
                <div className="mb-3">
                  <h4 className="text-xs font-medium text-slate-700 mb-2">Prerequisites:</h4>
                  <div className="flex flex-wrap gap-2">
                    {concept.relationships.requires.map((rel, idx) => (
                      <button
                        key={idx}
                        onClick={() => onRelatedClick?.(rel.targetConceptId)}
                        className="px-3 py-1 text-xs bg-blue-50 text-blue-700 rounded-full border border-blue-200 hover:bg-blue-100 transition-colors"
                      >
                        {rel.targetConceptId.replace(/_/g, ' ')}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {concept.relationships.enables && concept.relationships.enables.length > 0 && (
                <div className="mb-3">
                  <h4 className="text-xs font-medium text-slate-700 mb-2">Enables:</h4>
                  <div className="flex flex-wrap gap-2">
                    {concept.relationships.enables.map((rel, idx) => (
                      <button
                        key={idx}
                        onClick={() => onRelatedClick?.(rel.targetConceptId)}
                        className="px-3 py-1 text-xs bg-green-50 text-green-700 rounded-full border border-green-200 hover:bg-green-100 transition-colors"
                      >
                        {rel.targetConceptId.replace(/_/g, ' ')}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {concept.relationships.relatesTo && concept.relationships.relatesTo.length > 0 && (
                <div>
                  <h4 className="text-xs font-medium text-slate-700 mb-2">Related:</h4>
                  <div className="flex flex-wrap gap-2">
                    {concept.relationships.relatesTo.map((rel, idx) => (
                      <button
                        key={idx}
                        onClick={() => onRelatedClick?.(rel.targetConceptId)}
                        className="px-3 py-1 text-xs bg-purple-50 text-purple-700 rounded-full border border-purple-200 hover:bg-purple-100 transition-colors"
                      >
                        {rel.targetConceptId.replace(/_/g, ' ')}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Syntax Tab */}
      {activeTab === 'syntax' && (
        <div className="p-6 space-y-6">
          {/* Syntax Patterns */}
          {implementation.syntaxPatterns && implementation.syntaxPatterns.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3">Syntax Patterns</h3>
              <div className="space-y-3">
                {implementation.syntaxPatterns.map((pattern, idx) => (
                  <div key={idx} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="font-mono text-sm text-orange-600 mb-1">{pattern.syntax}</div>
                    <p className="text-sm text-slate-600">{pattern.explanation}</p>
                    {pattern.useCases && pattern.useCases.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {pattern.useCases.map((useCase, i) => (
                          <span key={i} className="text-xs px-2 py-0.5 bg-white rounded border border-slate-200 text-slate-600">
                            {useCase}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Code Snippets */}
          {implementation.codeSnippets && implementation.codeSnippets.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3">Code Examples</h3>
              <div className="space-y-4">
                {implementation.codeSnippets.map((snippet) => (
                  <CodeSnippet key={snippet.id} snippet={snippet} language={selectedLanguage} />
                ))}
              </div>
            </div>
          )}

          {/* Best Practices */}
          {implementation.bestPractices && implementation.bestPractices.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3">Best Practices</h3>
              <ul className="space-y-2">
                {implementation.bestPractices.map((practice, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                    <span className="text-green-500 flex-shrink-0">✓</span>
                    <span>{practice}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Common Pitfalls */}
          {implementation.commonPitfalls && implementation.commonPitfalls.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3">Common Pitfalls</h3>
              <ul className="space-y-2">
                {implementation.commonPitfalls.map((pitfall, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                    <span className="text-red-500 flex-shrink-0">⚠</span>
                    <span>{pitfall}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Idioms */}
          {implementation.idioms && implementation.idioms.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3">Common Idioms</h3>
              <ul className="space-y-2">
                {implementation.idioms.map((idiom, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                    <span className="text-blue-500 flex-shrink-0">→</span>
                    <span>{idiom}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Philosophy Tab */}
      {activeTab === 'philosophy' && concept.philosophy && (
        <div className="p-6">
          <PhilosophySection philosophy={concept.philosophy} />
        </div>
      )}
    </div>
  );
}
